import React, { useCallback, useState, useContext, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import styled from 'styled-components';
import {
  Avatar,
  Button,
  Heading,
  Tooltip as SuperopsTooltip,
  Modal,
  Sheet,
  DisplayText,
  Card,
  TextField,
  Page,
  TextStyle
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import envConfig from '@nile/home/src/Config/env';
import {
  getTranslatedText,
  COMMON_CONSTANTS,
  useProduct,
  isAuthorised,
  AppContext,
  SelectionSheet,
  toast,
  Loader,
  TEXT_LENGTH,
  withErrorBoundary
} from '@nile/components';
import { injectIntl, FormattedMessage } from 'react-intl';
import { TicketClient, GET_RUNBOOK_APPROVERS } from '@nile/graphql';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { isEmpty } from 'lodash';
import { TABLE_ROW_COUNT } from '@nile/rmm/src/constants';

function ApprovalContent(props) {
  const {
    intl,
    action,
    toggleSheetActive,
    ticket,
    runbookId,
    executeEntityFlowAction,
    sheetActive,
    onSheetClose,
    executeLoading
  } = props;

  const placeholder = getTranslatedText(intl, 'common.enter.your.message.here');

  // const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  // const appContext = useContext(AppContext);
  // const currentUser = appContext?.user || {};

  // const [selectedItems, setSelectedItems] = useState(
  //   action?.flowAction?.actionValueData.selectedItems || []
  // );
  const [selectedItems, setSelectedItems] = useState([]);

  const [sections, setSections] = useState([]);
  const [approvalMessage, setApprovalMessage] = useState('');

  const [listInfo, setListInfo] = useState({
    sort: [
      {
        attribute: 'name',
        order: 'ASC'
      }
    ]
  });

  const [isLoading, setIsLoading] = useState(false);

  const getApproversQuery = (enumType, actionId) => {
    //enum TECHNICIAN or REQUESTER
    return {
      query: GET_RUNBOOK_APPROVERS,
      variables: {
        input: {
          ticket: {
            ticketId: ticket.ticketId
          },
          runbook: {
            sopId: runbookId
          },
          flowAction: {
            actionId: actionId
          },
          roleType: enumType,
          listInfo: listInfo
        }
      },
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    };
  };

  async function getApproversList(action) {
    const { actionValueType, actionId } = action?.flowAction;

    try {
      setIsLoading(true);
      const [firstResponse, secondResponse] = await Promise.all([
        TicketClient.query(getApproversQuery('REQUESTER', actionId)),
        TicketClient.query(getApproversQuery('TECHNICIAN', actionId))
      ]);

      if (firstResponse?.data && secondResponse?.data) {
        setIsLoading(false);
        const requesters =
          firstResponse?.data?.getRunbookApprovers?.users || [];
        const technicians =
          secondResponse?.data?.getRunbookApprovers?.users || [];

        const sections = [];
        const requestersSection = {
          name: 'Requesters',
          header: 'Requesters',
          items: requesters.map((el) => ({
            id: el.userId || el,
            name: el.name || el,
            label: el.name || el,
            type: 'requesters'
          }))
        };

        const technicianSection = {
          name: 'Technicians',
          header: 'Technicians',
          items: technicians.map((el) => ({
            id: el.userId || el,
            name: el.name || el,
            label: el.name || el,
            type: 'technicians'
          }))
        };

        sections.push(requestersSection);
        sections.push(technicianSection);
        setSections(sections);
      }
    } catch (error) {
      console.error('Error fetching approvers:', error);
    }
  }
  useEffect(() => {
    getApproversList(props.action);
  }, []);

  useEffect(() => {
    if (!sheetActive) {
      //on sheet close set empty.
      setSelectedItems([]);
      setApprovalMessage('');
    }
  }, [sheetActive]);

  const save = (users) => {
    const result = {
      approval: {
        technicianIds: [],
        requesterIds: [],
        additionalMessage: approvalMessage
      }
    };

    users.forEach((item) => {
      if (item.type === 'requesters') {
        result.approval.requesterIds.push(item.id);
      } else result.approval.technicianIds.push(item.id);
    });

    executeEntityFlowAction(action?.flowAction?.actionId, result);
  };

  const updateParent = (users) => {
    setSelectedItems(users);
  };

  const handleSave = () => {
    save(selectedItems);
  };

  return (
    <div>
      <Sheet open={sheetActive} onClose={onSheetClose}>
        {isLoading ? (
          <div className="min-width-300 min-height-700 flex center">
            <Loader />
          </div>
        ) : (
          <Page
            contentHeight="120px"
            variant="secondary"
            // subtitle={
            //   <>
            //     <TextContainer>
            //       <TextStyle variation="subdued">
            //         <FormattedMessage id="admin.CauseAndSubcause.header.label" />
            //       </TextStyle>
            //     </TextContainer>
            //     <div className="mt10">
            //       <FeatureGuide featureName="cause" article />
            //     </div>
            //   </>
            // }
            title={
              <TextStyle weight="Bold">
                <FormattedMessage id="ticket.details.insights.initiateApprovals" />
              </TextStyle>
            }
            primaryAction={
              <Button
                primary
                icon={<SVGIcon name="SendWhite" />}
                onClick={handleSave}
                disabled={isEmpty(selectedItems)}
                loading={executeLoading}
              >
                <FormattedMessage id="app.create.email.send" />
              </Button>
            }
          >
            <div>
              <div className="px-20">
                <Card noShadow padding="sizeSmall" variant="secondaryCard">
                  <div className="mb5">
                    <FormattedMessage id="ticket.approvers.message.add.label" />
                  </div>

                  <div>
                    <TextField
                      multiline
                      placeholder={placeholder}
                      onChange={(val) => setApprovalMessage(val)}
                      value={approvalMessage}
                      bgTransparent
                      maxLength={TEXT_LENGTH.size_5}
                      maxHeight={80}
                    />
                  </div>
                </Card>
              </div>

              <SelectionSheet
                sections={sections}
                // actions={props.actions}
                meta={{
                  heading: getTranslatedText(
                    intl,
                    'ticket.details.insights.approvals.approvers.header'
                  ),
                  selectedTitle: 'Selected items'
                }}
                selectedItems={selectedItems}
                toggleSheetActive={toggleSheetActive}
                updateParent={updateParent}
                isRunbookAction
                disableScroll
                handleNodeDelete={null}
                hideSelectionSheetDoneBtn
              />
            </div>
          </Page>
        )}
      </Sheet>
    </div>
  );
}

export default withErrorBoundary(injectIntl(ApprovalContent));
